import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiTrendingUp, FiTarget, FiDollarSign, FiActivity, FiPercent, FiTrendingDown } = FiIcons

const PerformanceMetrics = ({ trades }) => {
  const metrics = useMemo(() => {
    if (!trades.length) {
      return { totalTrades: 0, winRate: 0, totalPnL: 0, profitFactor: 0, avgWin: 0, avgLoss: 0, expectancy: 0, maxDrawdown: 0 }
    }

    const winningTrades = trades.filter(t => (t.pnl || 0) > 0)
    const losingTrades = trades.filter(t => (t.pnl || 0) < 0)
    const totalTrades = trades.length
    const winRate = (winningTrades.length / totalTrades) * 100
    const totalPnL = trades.reduce((sum, t) => sum + (t.pnl || 0), 0)
    const totalWins = winningTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)
    const totalLosses = Math.abs(losingTrades.reduce((sum, t) => sum + (t.pnl || 0), 0))
    
    const profitFactor = totalLosses > 0 ? totalWins / totalLosses : (totalWins > 0 ? 999 : 0)
    const avgWin = winningTrades.length > 0 ? totalWins / winningTrades.length : 0
    const avgLoss = losingTrades.length > 0 ? totalLosses / losingTrades.length : 0
    const expectancy = totalTrades > 0 ? totalPnL / totalTrades : 0

    let peak = 0
    let maxDrawdown = 0
    let runningPnL = 0
    trades.forEach(trade => {
      runningPnL += trade.pnl || 0
      if (runningPnL > peak) peak = runningPnL
      const drawdown = peak - runningPnL
      if (drawdown > maxDrawdown) maxDrawdown = drawdown
    })

    return { totalTrades, winRate, totalPnL, profitFactor, avgWin, avgLoss, expectancy, maxDrawdown }
  }, [trades])

  const formatValue = (value, format) => {
    switch (format) {
      case 'currency':
        return `Rp ${Math.abs(value).toLocaleString('id-ID')}`
      case 'percentage':
        return `${value.toFixed(1)}%`
      case 'decimal':
        return value.toFixed(2)
      default:
        return value.toString()
    }
  }

  const metricsData = [
    { title: 'Total Trades', value: metrics.totalTrades, icon: FiActivity, color: 'blue', format: 'number' },
    { title: 'Win Rate', value: metrics.winRate, icon: FiTarget, color: metrics.winRate >= 50 ? 'green' : 'red', format: 'percentage' },
    { title: 'Total P&L', value: metrics.totalPnL, icon: FiDollarSign, color: metrics.totalPnL >= 0 ? 'green' : 'red', format: 'currency' },
    { title: 'Profit Factor', value: metrics.profitFactor, icon: FiTrendingUp, color: metrics.profitFactor >= 1 ? 'green' : 'red', format: 'decimal' },
    { title: 'Avg Win', value: metrics.avgWin, icon: FiTrendingUp, color: 'green', format: 'currency' },
    { title: 'Avg Loss', value: metrics.avgLoss, icon: FiTrendingDown, color: 'red', format: 'currency' },
    { title: 'Expectancy', value: metrics.expectancy, icon: FiPercent, color: metrics.expectancy >= 0 ? 'green' : 'red', format: 'currency' },
    { title: 'Max Drawdown', value: metrics.maxDrawdown, icon: FiTrendingDown, color: 'red', format: 'currency' }
  ]

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'text-blue-600 bg-blue-100',
      green: 'text-green-600 bg-green-100',
      red: 'text-red-600 bg-red-100',
      purple: 'text-purple-600 bg-purple-100'
    }
    return colorMap[color] || colorMap.blue
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Metrics</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricsData.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                <p className={`text-xl font-bold mt-1 ${metric.title.includes('P&L') || metric.title.includes('Avg') ? (metric.value >= 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-900'}`}>
                  {metric.value < 0 && metric.format === 'currency' ? '-' : ''}
                  {formatValue(metric.value, metric.format)}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${getColorClasses(metric.color)}`}>
                <SafeIcon icon={metric.icon} className="h-5 w-5" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

export default PerformanceMetrics